import { useMutation, useQuery } from '@tanstack/react-query';
import { canInAnyScope, scopeFor } from '@testflow/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { digestApi } from '../../lib/digest-api.js';
import { defaultActivePlanId } from '../../lib/plan-selection.js';
import { plansApi } from '../../lib/plans-api.js';
import { useScopedUser } from '../../lib/project-roles.js';
import { useDigestHistory, useDigestSendNow } from '../../lib/use-digest-runs.js';
import { useCurrentProject } from '../../lib/use-project.js';

// Task C7 (Plan 7, Gruppe C): reine Historie-/Vorschau-Sicht auf den Weekly Digest (NOT-5). Die
// Admin-Einstellungen (Zeitplan, Empfänger, Sections) sind bewusst NICHT hier — das ist Task F4.
export function DigestHistoryPage() {
  const { t } = useTranslation();
  const { project, isLoading: projectLoading } = useCurrentProject();
  const projectId = project?.project_id;
  // Fund I2 (Nachpruefung): dieselbe Klasse wie die Digest-Vorschau auf /reporting — die
  // Historie zeigt vergangene digest_runs (buildDigestData), die nie einen Knoten-Zuschnitt
  // kannten. #23: Rollen ueber `useScopedUser(projectId)`, nie `useSession()` roh, sonst urteilt
  // die Zeile projektuebergreifend. `scopeFor` (nicht `scopeHintFrom`) reicht hier, weil der
  // Hinweistext keine Knotennamen braucht — nur ob ueberhaupt ein Zuschnitt aktiv ist.
  const { user } = useScopedUser(projectId);
  const scopeActive = user ? scopeFor(user, 'report.read')?.kind === 'nodes' : false;

  const [panel, setPanel] = useState<{
    heading: string;
    kind: 'history' | 'preview';
    html: string;
  } | null>(null);

  const plansQuery = useQuery({
    queryKey: ['plans', projectId],
    queryFn: () => plansApi.list(projectId!),
    enabled: Boolean(projectId),
  });
  // Task 6 (#55): mehrere Testpläne dürfen gleichzeitig aktiv sein, also entscheidet nicht mehr
  // der ERSTE aktive Treffer in Listenreihenfolge (`find(is_active) ?? plans[0]`), sondern die
  // fachliche Vorrangregel aus plan-selection.ts (nächster Zieltermin) — dieselbe Regel wie auf
  // der Sign-off-Seite (signoff-page.tsx) und im Digest-Admin-Panel (digest-panel.tsx).
  const plans = plansQuery.data ?? [];
  const activePlanId = defaultActivePlanId(plans);

  const historyQuery = useDigestHistory(projectId);
  const resend = useDigestSendNow(projectId);
  const canResend = user ? canInAnyScope(user, 'project.configure') : false;
  const runs = [...(historyQuery.data ?? [])].sort((a, b) =>
    b.generated_at.localeCompare(a.generated_at),
  );

  // Task C7 review: über useMutation statt "plain async + void" laufen lassen, damit Fehler (403/
  // 404/Netzwerk) automatisch durch den globalen Error-Toast (MutationCache.onError,
  // query-client.ts) sichtbar werden statt lautlos verschluckt zu werden.
  const openRunMutation = useMutation({
    mutationFn: ({ runId }: { runId: string; subject: string }) =>
      digestApi.historyHtml(projectId!, runId),
    onSuccess: ({ html }, { subject }) => {
      setPanel({ heading: subject, kind: 'history', html });
    },
  });

  const openPreviewMutation = useMutation({
    mutationFn: () => digestApi.preview(projectId!, activePlanId!),
    onSuccess: ({ html }) => {
      setPanel({ heading: t('digest.preview'), kind: 'preview', html });
    },
  });

  if (projectLoading) return <div className="p-6 text-text-muted">{t('common.loading')}</div>;
  if (!project) return <div className="p-6 text-text-muted">{t('reporting.noProject')}</div>;

  return (
    <div className="space-y-4 p-6">
      {/* #32 (Finding 7): Rückweg. Die Seite ist nur über das Reporting erreichbar, hatte aber
          keinen Ausgang — wer hier landete, kam nur über den Browser-Zurück-Knopf heraus. */}
      <Link
        to="/reporting"
        className="inline-block text-xs text-text-muted hover:text-brand-accent"
      >
        ← {t('digest.back')}
      </Link>
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold text-text-primary">{t('digest.title')}</h1>
        <button
          type="button"
          onClick={() => openPreviewMutation.mutate()}
          disabled={!activePlanId}
          className="rounded-btn border border-brand-accent px-3 py-1 text-xs text-brand-accent disabled:opacity-50"
        >
          {t('digest.preview')}
        </button>
      </div>

      {/* Fund I2 (Nachpruefung): frühere Digest-Läufe waren immer projektweit — ohne diese Zeile
          liest ein zugeschnittener Verantwortlicher die Historie als "sein" Teilprojekt. Nur
          sichtbar, wenn ein Zuschnitt aktiv ist; ein projektweiter Nutzer sieht sie nie. */}
      {scopeActive ? (
        <p className="text-xs text-text-muted" data-testid="scope-hint-digest">
          {t('scope.hintDigest')}
        </p>
      ) : null}

      <section className="rounded-card border border-[var(--border)] bg-bg-surface p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-bold text-text-primary">{t('digest.history')}</h2>
        </div>

        {historyQuery.isLoading ? (
          <div className="text-sm text-text-muted">{t('common.loading')}</div>
        ) : null}

        {!historyQuery.isLoading && runs.length === 0 ? (
          <div className="text-sm text-text-muted">{t('digest.noRuns')}</div>
        ) : null}

        <ul className="divide-y divide-[var(--border)]">
          {runs.map((run, index) => {
            // #160: Die Empfängerzahl schreibt erst der Abschluss des Laufs. Laufend oder abgebrochen
            // stünde sonst „0 Empfänger“ da; ein Fehlschlag vor dem Versand (0) ebenso.
            const showRecipients =
              run.status !== 'running' &&
              run.status !== 'interrupted' &&
              (run.status !== 'failed' || run.recipient_count > 0);
            const meta = (
              <span className="text-xs text-text-muted">
                {t('digest.window', { window: run.window })} ·{' '}
                {showRecipients ? (
                  <>{t('digest.recipients', { count: run.recipient_count })} · </>
                ) : null}
                {t(`digest.status.${run.status}`)}
              </span>
            );
            // #160: Ein laufender Versand hat noch keinen Schnappschuss — kein Knopf, nur Anzeige.
            // „Erneut senden“ gibt es nur am jüngsten Eintrag: der Versand nimmt immer die aktuelle
            // Woche, ein älterer Fehlschlag ließe sich damit nicht nachholen.
            const showResend =
              canResend && index === 0 && (run.status === 'failed' || run.status === 'interrupted');
            return (
              <li key={run.digest_run_id}>
                {run.status === 'running' ? (
                  <div className="flex w-full items-center justify-between gap-3 py-2 text-sm">
                    <span className="text-text-primary">{run.subject}</span>
                    {meta}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() =>
                      openRunMutation.mutate({ runId: run.digest_run_id, subject: run.subject })
                    }
                    className="flex w-full items-center justify-between gap-3 py-2 text-left text-sm hover:text-brand-accent"
                  >
                    <span className="text-text-primary">{run.subject}</span>
                    {meta}
                  </button>
                )}
                {showResend ? (
                  <div className="flex items-center gap-3 pb-2">
                    {/* #160: „Erneut senden“ verschickt immer die aktuelle Woche — auch an einem
                        Fehlschlag aus einer früheren. Der Hinweis steht bei failed immer: Die
                        aktuelle Woche bestimmt der Server in der Zeitzone des Projekts, ein
                        Nachbau im Client könnte an der Wochengrenze abweichen. */}
                    <p className="text-xs text-text-muted">
                      {run.status === 'interrupted'
                        ? t('digest.interruptedHint')
                        : t('digest.resendHint')}
                    </p>
                    <button
                      type="button"
                      onClick={() => resend.mutate()}
                      disabled={resend.isPending}
                      className="shrink-0 rounded-btn border border-brand-accent px-3 py-1 text-xs text-brand-accent disabled:opacity-50"
                    >
                      {t('digest.resend')}
                    </button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </section>

      {panel ? (
        <section className="rounded-card border border-[var(--border)] bg-bg-surface p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-bold text-text-primary">{panel.heading}</h2>
            <button
              type="button"
              onClick={() => setPanel(null)}
              aria-label={t('common.close')}
              className="text-xs text-text-muted hover:text-text-primary"
            >
              ✕
            </button>
          </div>
          <iframe
            title={panel.kind === 'preview' ? t('digest.preview') : t('digest.history')}
            srcDoc={panel.html}
            className="h-[600px] w-full"
          />
        </section>
      ) : null}
    </div>
  );
}
